import { useEffect, useState } from 'react';

/**
 * PWA (docs/phases/X-PWA.md): cài ERP lên màn hình chính như một ứng dụng.
 *  - Android / Chrome / Edge: trình duyệt bắn sự kiện `beforeinstallprompt`; giữ lại để nút "Cài ứng dụng" gọi prompt().
 *  - iPhone / iPad (Safari): không có sự kiện này, phải hướng dẫn "Chia sẻ → Thêm vào MH chính".
 * Sự kiện có thể bắn trước khi React vẽ xong, nên bắt ngay khi file này được nạp (main.tsx import sớm).
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // tự hiện nút trong ERP thay cho thanh gợi ý của Chrome
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

/** Đang chạy dạng ứng dụng đã cài (mở từ biểu tượng trên màn hình chính). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

/** iPhone / iPad (kể cả iPad đời mới báo là "Macintosh" nhưng có màn cảm ứng). */
export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export type InstallMode = 'installed' | 'prompt' | 'ios' | 'manual';

export function getInstallMode(): InstallMode {
  if (isStandalone()) return 'installed';
  if (deferred) return 'prompt';
  if (isIos()) return 'ios';
  return 'manual';
}

/** Gọi hộp cài của trình duyệt. Trả true nếu người dùng đồng ý cài. */
export async function promptInstall(): Promise<boolean> {
  const e = deferred;
  if (!e) return false;
  await e.prompt();
  const { outcome } = await e.userChoice;
  // Mỗi sự kiện chỉ prompt được một lần
  deferred = null;
  notify();
  return outcome === 'accepted';
}

export function useInstallMode(): InstallMode {
  const [mode, setMode] = useState<InstallMode>(getInstallMode);
  useEffect(() => {
    const l = () => setMode(getInstallMode());
    listeners.add(l);
    const mq = window.matchMedia?.('(display-mode: standalone)');
    mq?.addEventListener?.('change', l);
    return () => {
      listeners.delete(l);
      mq?.removeEventListener?.('change', l);
    };
  }, []);
  return mode;
}

// ---------------------------------------------------------------------------
// Bước 2: service worker (vỏ app chạy khi mất mạng) + trạng thái mạng
// ---------------------------------------------------------------------------

/**
 * Đăng ký /sw.js (chỉ bản build; dev không có). Có bản mới: gọi onUpdate(apply) để trang hỏi người dùng,
 * apply() cho bản mới chạy rồi tải lại trang. Không tự tải lại để không mất báo giá đang soạn.
 */
export function registerServiceWorker(onUpdate: (apply: () => void) => void) {
  if (!import.meta.env.PROD || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) window.location.reload();
  });
  const offer = (w: ServiceWorker) =>
    onUpdate(() => {
      reloading = true;
      w.postMessage({ type: 'SKIP_WAITING' });
    });
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        // Lần cài đầu (chưa có controller) không hỏi; chỉ hỏi khi thay bản cũ
        if (reg.waiting && navigator.serviceWorker.controller) offer(reg.waiting);
        reg.addEventListener('updatefound', () => {
          const w = reg.installing;
          w?.addEventListener('statechange', () => {
            if (w.state === 'installed' && navigator.serviceWorker.controller) offer(w);
          });
        });
        // Ứng dụng để mở cả ngày trên điện thoại: kiểm bản mới mỗi giờ
        setInterval(() => void reg.update().catch(() => undefined), 60 * 60_000);
      })
      .catch((e) => console.warn('Không đăng ký được service worker', e));
  });
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/**
 * Bước 3: đăng xuất thì gỡ thông báo đẩy của máy này (máy dùng chung: người sau không nhận thông báo của người trước).
 * Không bao giờ ném lỗi, tối đa 3 giây.
 */
export async function dropDevicePush(removeOnServer: (endpoint: string) => Promise<unknown>) {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return;
  const work = (async () => {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager?.getSubscription();
    if (!sub) return;
    await removeOnServer(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe().catch(() => undefined);
  })().catch(() => undefined);
  await Promise.race([work, new Promise((r) => setTimeout(r, 3000))]);
}
